import { Text, Title } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import {
  ArrowUpLine,
  DropboxDashLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import {
  useFeatureFlagValue,
  useFeatureFlagValueBool,
} from "@mirage/service-experimentation/useFeatureFlagValue";
import { openURL } from "@mirage/service-platform-actions";
import { BetaBadge } from "@mirage/shared/badges/BetaBadge";
import { TwoColumnGrid } from "@mirage/shared/two-column-grid/TwoColumnGrid";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import { isEmbedPage } from "@mirage/shared/util/tiny-utils";
import i18n from "@mirage/translations";
import cx from "classnames";
import { memo } from "react";
import styles from "./SidePanelContainer.module.css";

export interface SidePanelContainerProps {
  leftTitleActions?: React.ReactNode;
  rightTitleActions?: React.ReactNode;
  hideTitle?: boolean;
  sidebarChildren?: React.ReactNode;
  children: React.ReactNode;
  handleClickAdd?: () => void;
  sources?: ChatSource[];
  showWorkflowAgent: boolean;
}
export const SidePanelContainer = memo(
  ({
    leftTitleActions,
    rightTitleActions,
    hideTitle,
    sidebarChildren,
    children,
    handleClickAdd,
    sources,
    showWorkflowAgent,
  }: SidePanelContainerProps) => {
    const isCategoriesEnabled =
      useFeatureFlagValue("dash_2025_03_05_serp_categories") === "V1";
    const isConversationAssistantEnabled = useFeatureFlagValueBool(
      "dash_2025_02_11_conversation_assistant",
    );
    const isChatEmbedPage = isEmbedPage();

    return (
      <div
        className={cx(styles.container, {
          [styles.embedContainer]: isChatEmbedPage,
        })}
      >
        <div
          className={cx(styles.titleContainer, {
            [styles.embedTitleContainer]: isChatEmbedPage,
          })}
        >
          {isChatEmbedPage ? (
            <>
              <div className={styles.titleActionsWrapper}>
                <DigTooltip
                  title={i18n.t("open_dash_in_new_tab")}
                  placement="bottom"
                >
                  <button
                    className={styles.embedTitleAndLogo}
                    onClick={() => {
                      openURL("https://www.dash.ai/chat");
                    }}
                  >
                    <UIIcon src={DropboxDashLine} size="large" />
                    <div className={styles.titleAndArrow}>
                      <Title size="small" className={styles.title}>
                        {i18n.t("dash")}
                      </Title>
                      <UIIcon src={ArrowUpLine} />
                    </div>
                  </button>
                </DigTooltip>
              </div>
              {leftTitleActions && (
                <div className={styles.titleActionsEmbedWrapper}>
                  <div className={styles.titleActions}>{leftTitleActions}</div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className={styles.titleActionsWrapper}>
                {leftTitleActions && (
                  <div className={styles.titleActions}>{leftTitleActions}</div>
                )}
                {!hideTitle && (
                  <div className={styles.titleAndChip}>
                    <Title size="small" className={styles.title}>
                      {showWorkflowAgent
                        ? i18n.t("assistant_card_title_create_ai_tool")
                        : i18n.t("compose_assistant_title")}
                    </Title>
                    <div className={styles.titleChip}>
                      <BetaBadge />
                    </div>
                    {sources && sources.length > 0 && (
                      <>
                        <div className={styles.sourceCountSpacer} />
                        <Text
                          size="medium"
                          onClick={handleClickAdd}
                          className={styles.sourceCount}
                        >
                          {i18n.t("compose_num_sources", {
                            count: sources.length,
                          })}
                        </Text>
                      </>
                    )}
                  </div>
                )}
              </div>
              {rightTitleActions && (
                <div className={styles.titleActionsWrapper}>
                  <div className={styles.titleActions}>{rightTitleActions}</div>
                </div>
              )}
            </>
          )}
        </div>
        {isConversationAssistantEnabled &&
        (isCategoriesEnabled || sidebarChildren) ? (
          <TwoColumnGrid
            className={styles.twoColumnGridContainer}
            columnClassName={
              sidebarChildren
                ? styles.twoColumnGridClass
                : styles.twoColumnGridClassSingleColumn
            }
            columnGroupClassName={styles.height100}
            mainChildren={children}
            sidebarChildren={
              sidebarChildren && (
                <div className={styles.sidebarContainer}>{sidebarChildren}</div>
              )
            }
          />
        ) : (
          children
        )}
      </div>
    );
  },
);
SidePanelContainer.displayName = "SidePanelContainer";
